import { useMemo } from "react";
import { addDays, endOfMonth, endOfWeek, isSameDay, isSameMonth, isWithinInterval, parseISO, startOfMonth, startOfWeek } from "date-fns";
import { cn } from "@/lib/utils";
import { WEEKDAYS_SHORT, toISODate } from "@/lib/format";
import { practicesOn } from "@/lib/recurring";

export const MonthGrid = ({ month, entries = [], recurring = [], roomId = null, selectedDay, onSelectDay, testId = "public-calendar-month-grid" }) => {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    const out = [];
    let d = start;
    while (d <= end) { out.push(d); d = addDays(d, 1); }
    return out;
  }, [month]);
  const today = new Date();
  const entriesFor = (day) => entries.filter((e) => isWithinInterval(day, { start: parseISO(e.start_date), end: parseISO(e.end_date || e.start_date) }));
  const recurringFor = (day) => practicesOn(recurring, day, roomId);

  return (
    <div className="rounded-[20px] border border-border bg-card p-2 sm:p-3" data-testid={testId}>
      <div className="grid grid-cols-7">
        {WEEKDAYS_SHORT.map((w) => <div key={w} className="px-2 py-2 t-mono text-muted-foreground">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const inMonth = isSameMonth(day, month);
          const dayEntries = inMonth ? entriesFor(day) : [];
          const dayRec = inMonth ? recurringFor(day) : [];
          const isToday = isSameDay(day, today);
          const selected = selectedDay && isSameDay(day, selectedDay);
          const whole = dayEntries.some((e) => e.is_whole_house);
          const busy = dayEntries.length > 0;
          return (
            <button
              type="button"
              key={day.toISOString()}
              onClick={() => inMonth && onSelectDay?.(day)}
              disabled={!inMonth}
              data-testid={inMonth ? `calendar-day-${toISODate(day)}` : undefined}
              className={cn(
                "relative min-h-[60px] rounded-[10px] p-1.5 text-left transition-colors sm:min-h-[104px] sm:p-2 focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground",
                !inMonth && "pointer-events-none opacity-40",
                inMonth && !busy && "bg-background hover:bg-secondary",
                inMonth && busy && !whole && "bg-secondary hover:bg-border/70",
                inMonth && whole && "stripes bg-secondary",
                selected && "ring-2 ring-foreground"
              )}
            >
              <div className="flex items-start justify-between">
                <span className={cn("tabular text-[13px] leading-none", isToday && "inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-foreground px-1.5 text-background")}>{day.getDate()}</span>
                {inMonth && dayRec.length > 0 && <span className="h-1.5 w-1.5 rounded-full bg-sol sm:hidden" />}
              </div>
              <div className="mt-2 hidden flex-col gap-1 sm:flex">
                {dayEntries.slice(0, 2).map((e) => <span key={e.id} className={cn("truncate rounded-[6px] px-2 py-0.5 text-[11px] leading-4", e.visibility === "public" ? "bg-foreground text-background" : "bg-foreground/[0.07] text-muted-foreground")} title={e.title}>{e.title}</span>)}
                {dayEntries.length > 2 && <span className="px-2 t-mono-sm text-muted-foreground">+{dayEntries.length - 2} til</span>}
                {dayRec.slice(0, dayEntries.length > 1 ? 0 : 1).map((r) => <span key={`${r.id}-${r.occurrence_date}`} className="truncate rounded-[6px] bg-sol/25 px-2 py-0.5 text-[11px] leading-4 text-foreground" title={`${r.title} · kl. ${r.start_time}–${r.end_time}`} data-testid={`month-grid-recurring-${toISODate(day)}`}>{r.title}</span>)}
              </div>
              <div className="mt-1.5 flex gap-1 sm:hidden">
                {dayEntries.slice(0, 3).map((e) => <span key={e.id} className={cn("h-1.5 w-1.5 rounded-full", e.visibility === "public" ? "bg-foreground" : "bg-muted-foreground")} />)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
